/**
 * THE AUTHOR HUB
 * ──────────────
 * The signed-in landing surface for authors. Three product cards — Publishing
 * Checklist, Marketing Compass, Story Bible — each showing its real licence
 * state, plus feature-flag-driven "Coming soon" states for surfaces that aren't
 * live yet.
 *
 * Rendered at the top of the `overview` tab of /author/dashboard (the existing
 * post-sign-in path — no new route). The dashboard's own overview content
 * follows underneath; the hub is additive per spec §2.
 *
 * Design note: the spec asks for the Roadmap's ink/parchment/gold language, but
 * this renders INSIDE the slate/teal author dashboard. Dropping a dark ink panel
 * into that shell would read as a broken embed, so the cards keep the dashboard's
 * slate/teal base and borrow gold only as the accent for paid/licence states —
 * the one visual cue the two brands already share.
 */
import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { getMyLicenceFn } from '../licensing/licenceApi';
import { useFeatureFlags } from '../config/featureFlags';
import { ACCESS } from './hubAccess';
import FeedbackBanner from '../marketing/FeedbackBanner';
import * as T from '../brand/tokens';
import {
  ClipboardCheck, Compass, BookMarked, Lock, ArrowRight, Clock, Sparkles,
} from 'lucide-react';

/* ── Licence state ──────────────────────────────────────── */

/**
 * Resolve THE licence — singular.
 *
 * ONE PLAN: the Publishing Checklist, Marketing Compass and Story Bible are all
 * included in the single Narratoria plan, so there is exactly one entitlement
 * question to ask. `anyTool: true` makes the server accept any active scope
 * (roadmap, planner or bundle).
 *
 * ⚠️ This used to fire TWO calls — one for 'roadmap' and one for 'planner',
 * neither passing anyTool — which meant the Story Bible card asked "do you hold
 * a planner-or-bundle licence?" while the Story Bible TOOL asked "do you hold
 * any active licence?". A roadmap trial therefore saw a locked card offering
 * to sell them something the tool would have opened. One call for one plan
 * makes that divergence structurally impossible.
 *
 * Fails closed — any error leaves every card locked, never unlocked.
 */
function useHubLicences() {
  const [state, setState] = useState({ loading: true, licence: null, user: null });

  useEffect(() => {
    let cancelled = false;

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (cancelled) return;

      if (!user) {
        setState({ loading: false, licence: null, user: null });
        return;
      }

      let licence;
      try {
        // `product` is irrelevant when anyTool is set — the server ignores it.
        const res = await getMyLicenceFn({ product: 'roadmap', anyTool: true });
        licence = res?.data || null;
      } catch (err) {
        console.error('[hub] getMyLicence failed:', err?.code, err?.message);
        licence = { error: true };
      }

      if (!cancelled) setState({ loading: false, licence, user });
    });

    return () => { cancelled = true; unsub(); };
  }, []);

  return state;
}

/** Map a raw getMyLicence payload to one of the ACCESS states. */
function accessStateOf(lic, user) {
  if (!user) return ACCESS.SIGNED_OUT;
  if (!lic) return ACCESS.LOADING;
  if (lic.error) return ACCESS.ERROR;
  if (!lic.found) return ACCESS.NONE;
  if (lic.expired) return ACCESS.EXPIRED;
  return ACCESS.GRANTED;
}

/**
 * Expiry chip. Copy is deliberately EXPIRY-based, never "renews" — these are
 * one-off timed licences with no auto-renewal, so the wording has to match the
 * pricing promise. Mirrors the Roadmap's own chip logic.
 */
function LicenceChip({ licence, state }) {
  if (state === ACCESS.EXPIRED) {
    return <Chip tone="rose">{licence?.term === 'trial' ? 'Trial ended' : 'Expired'}</Chip>;
  }
  if (state !== ACCESS.GRANTED) return null;

  const ms = licence?.expiresAtMs;
  const isTrial = licence?.term === 'trial';

  if (ms == null) return <Chip tone="teal">Licensed</Chip>;

  const left = ms - Date.now();
  if (left <= 0) return <Chip tone="rose">Expired</Chip>;

  const days = Math.floor(left / 86400000);
  const today = days <= 0;

  if (isTrial) {
    return (
      <Chip tone={days <= 3 ? 'rose' : 'gold'}>
        {today ? 'Trial — expires today' : `Trial — ${days} day${days === 1 ? '' : 's'} left`}
      </Chip>
    );
  }

  // Paid licences stay quiet until the last 30 days, then start counting down.
  if (days > 30) {
    const on = new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return <Chip tone="teal">{`Licensed until ${on}`}</Chip>;
  }

  return (
    <Chip tone={days <= 7 ? 'gold' : 'muted'}>
      {today ? 'Expires today' : `Expires in ${days} day${days === 1 ? '' : 's'}`}
    </Chip>
  );
}

// Gold uses the Narratoria brand tokens from tailwind.config.js (gold.soft /
// gold.deep), NOT Tailwind's stock amber palette — amber is banned repo-wide.
const CHIP_TONES = {
  teal:   'border-[#2f5a34]/30 bg-[#2f5a34]/10 text-[#2f5a34]',
  gold:   'border-gold-soft bg-gold/12 text-gold-deep',
  rose:   'border-[#8a3a1e]/30 bg-[#8a3a1e]/10 text-[#8a3a1e]',
  muted:  'border-[#d9cba6] bg-parchment-tint text-[#6d6151]',
  locked: 'border-[#d9cba6] bg-parchment-tint text-[#6d6151]',
};

function Chip({ tone = 'muted', children }) {
  return (
    <span className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${CHIP_TONES[tone]}`}>
      {children}
    </span>
  );
}

/* ── Card ───────────────────────────────────────────────── */

function HubCard({
  stage, title, blurb, icon: Icon, href, state, licence,
  comingSoon, comingSoonNote, lockedCta, valueProp,
}) {
  if (comingSoon) {
    return (
      <div className="flex h-full flex-col rounded-2xl border border-gold-soft/50 bg-parchment/70">
        <div className="flex h-full flex-col gap-3 p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gold-soft bg-gold/8 text-gold-deep/60">
              <Icon className="h-5 w-5" />
            </div>
            <Chip tone="muted">Coming soon</Chip>
          </div>
          <div>
            {stage && <p className={T.MICRO + " text-gold-deep/70"}>{stage}</p>}
            <h3 className={"font-cinzel text-base font-bold text-[#241f17]/60"}>{title}</h3>
            <p className={"mt-1 leading-relaxed text-[#6d6151]"}>{comingSoonNote || blurb}</p>
          </div>
        </div>
      </div>
    );
  }

  const loading = state === ACCESS.LOADING;
  const unlocked = state === ACCESS.GRANTED;
  const expired = state === ACCESS.EXPIRED;
  const errored = state === ACCESS.ERROR;
  const locked = state === ACCESS.NONE || state === ACCESS.SIGNED_OUT;

  return (
    <div className="flex h-full flex-col rounded-2xl border border-gold-soft bg-parchment shadow-lg transition-shadow hover:shadow-2xl">
      <div className="flex h-full flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gold-soft bg-gold/15 text-gold-deep">
            <Icon className="h-5 w-5" />
          </div>
          {loading
            ? <Chip tone="muted">Checking…</Chip>
            : locked
              ? <Chip tone="locked"><Lock className="mr-1 h-3 w-3" />Locked</Chip>
              : <LicenceChip licence={licence} state={state} />}
        </div>

        {/* min-w-0 lets the text column shrink inside the flex row — without it
            a long product name can push the card wider than a 380px viewport. */}
        <div className="min-w-0 flex-1">
          {stage && <p className={T.MICRO + " text-gold-deep"}>{stage}</p>}
          <h3 className={T.H3}>{title}</h3>
          <p className={"mt-1 leading-relaxed " + T.BODY_MUTED}>
            {locked && valueProp ? valueProp : blurb}
          </p>
        </div>

        {errored ? (
          <p className={"text-sm " + T.BODY_FAINT}>
            Couldn't check your licence just now.{' '}
            <button onClick={() => window.location.reload()} className="font-semibold text-gold-deep underline">
              Retry
            </button>
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {(unlocked || expired) && (
              <button
                type="button"
                className={expired ? T.BTN_SECONDARY : T.BTN_PRIMARY}
                onClick={() => { window.location.href = href; }}
              >
                {expired ? 'Renew' : 'Open'} <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
            {locked && (
              <button
                type="button"
                className={T.BTN_SECONDARY}
                onClick={() => { window.location.href = `${href}?view=plans`; }}
              >
                {lockedCta || 'View plans'} <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
            {loading && <button type="button" disabled className={T.BTN_SECONDARY}>Loading…</button>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Hub ────────────────────────────────────────────────── */

export default function AuthorHub({ onEntitlementResolved }) {
  const { flags, loading: flagsLoading } = useFeatureFlags();
  const { loading: licLoading, licence, user } = useHubLicences();

  const loading = flagsLoading || licLoading;

  // ONE PLAN, ONE ENTITLEMENT — every card reads the same state. Splitting this
  // is what let the Story Bible card and the Story Bible tool disagree.
  const access = loading ? ACCESS.LOADING : accessStateOf(licence, user);
  const roadmapState = access;
  const marketingState = access;
  const plannerState = access;

  // Report upward whether ANYTHING is entitled, so the page around the hub can
  // decide what to say (e.g. the verify-your-email prompt only makes sense when
  // nothing unlocked). Cheaper than a second round of licence calls.
  useEffect(() => {
    if (loading || !onEntitlementResolved) return;
    onEntitlementResolved(access === ACCESS.GRANTED);
  }, [loading, access, onEntitlementResolved]);

  return (
    <section aria-label="Your tools">
      {/* 30-day feedback prompt. Renders nothing unless the server says one is
          due, and never blocks the cards below it. */}
      {flags.marketingCompass && <FeedbackBanner />}

      <div className="mb-4">
        <h2 className={T.MICRO + " text-gold"}>Your tools</h2>
        <p className={"mt-1 " + T.ON_INK}>Everything included with your Narratoria plan.</p>
      </div>

      {/* Mobile-first: one column, stacking to a 3-up grid from `sm`. */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <HubCard
          stage="Pre-launch"
          title="Story Bible"
          icon={BookMarked}
          href="/plot-planner"
          state={plannerState}
          licence={licence?.licence}
          comingSoon={!flags.storyBible}
          comingSoonNote="Plan your novel before you draft it — characters, world, dynasty map, plot spine and timeline."
          blurb="Plan any novel before you draft — 12 sections, from world to timeline."
          valueProp="The pre-writing workbook — plan any novel before you draft it."
        />

        <HubCard
          stage="Launch"
          title="Publishing Checklist"
          icon={ClipboardCheck}
          href="/publishing-roadmap"
          state={roadmapState}
          licence={licence?.licence}
          blurb="Your step-by-step path to publication — 5 phases, 37 tasks, 25 guides."
          valueProp="The full self-publishing checklist, guides, metadata and vault."
        />

        <HubCard
          stage="Post-launch"
          title="Marketing Compass"
          icon={Compass}
          href="/marketing"
          state={marketingState}
          licence={licence?.licence}
          comingSoon={!flags.marketingCompass}
          comingSoonNote="Answer a few questions and get a marketing tool stack picked for your book, your genre and your budget."
          blurb="A personalised marketing tool stack — matched to your genre, stage and budget."
          valueProp="Answer ~16 questions and get a marketing tool stack matched to your book. Included with your plan."
          lockedCta="Included with your plan"
        />
      </div>

      {/* Only shown once we know the user genuinely has nothing. */}
      {!loading && access === ACCESS.NONE && (
        <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-gold-soft bg-white/[0.03] px-4 py-3">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
          <p className={"leading-relaxed " + T.ON_INK}>
            These tools are one-off timed licences — no subscription, no auto-renewal.
            A free 10-day trial is available for each.
          </p>
        </div>
      )}
    </section>
  );
}

export { LicenceChip, HubCard, useHubLicences, accessStateOf };
